/**
 * Internal linking strategy for the Recovery Trauma™ blog.
 *
 * Structure: hub-and-spoke topic clusters. Each cluster has one pillar page
 * (the post we want to rank for the head term) and a set of spokes that all
 * link up to it with descriptive, keyword-bearing anchor text, plus sideways
 * to their closest siblings. Bridges connect clusters where the reader intent
 * genuinely overlaps (e.g. breaking a trauma bond -> regulating the body).
 *
 * Anchor text is deliberate: it is the strongest on-page signal we control for
 * the pages currently sitting on page 2-3 ("trauma bond", "generational
 * trauma", "54321 method", "complex PTSD").
 */

export type ClusterId =
  | "trauma-bonds"
  | "generational-childhood"
  | "grounding-regulation"
  | "ptsd-cptsd"
  | "overcoming-trauma"
  | "recovery-basics";


export interface ClusterDef {
  id: ClusterId;
  /** Head term this cluster is built to rank for. */
  headTerm: string;
  /** Human label used in the on-page "part of" line. */
  label: string;
  /** Pillar post slug — every spoke links up to this. */
  pillar: string;
  /** Spoke slugs in priority order. */
  spokes: string[];
  /** Slugs from other clusters worth linking to from this one. */
  bridges?: string[];
}

/** Descriptive anchor text per slug. Keep keyword-bearing and human. */
export const ANCHORS: Record<string, string> = {
  // Trauma bonds & abusive relationships
  "trauma-bonding-explained": "what a trauma bond is and how to break free",
  "how-to-break-a-trauma-bond": "how to break a trauma bond, step by step",
  "what-is-emotional-abuse-signs-examples": "what emotional abuse is, with signs and examples",
  "fawn-response-people-pleasing-trauma": "the fawn response and people-pleasing after trauma",
  "why-you-miss-them-withdrawal-after-trauma-bonding": "trauma bond withdrawal and why you miss them",
  "trauma-bonding-with-a-parent": "trauma bonding with a parent",
  "the-cycle-of-abuse-explained": "the cycle of abuse explained",
  "love-bombing-the-first-red-flag": "love bombing, the first red flag",
  "narcissistic-abuse-recognising-the-patterns": "recognising the patterns of narcissistic abuse",
  "coercive-control-the-quiet-kind-of-abuse": "coercive control, the quiet kind of abuse",
  "gaslighting-how-to-trust-yourself-again": "gaslighting and how to trust yourself again",
  "no-contact-vs-grey-rock": "no contact vs grey rock",
  "why-its-so-hard-to-leave-an-abusive-relationship": "why it's so hard to leave an abusive relationship",
  "how-trauma-affects-relationships": "how trauma affects relationships",
  "rebuilding-your-sense-of-self-after-abuse": "rebuilding your sense of self after abuse",
  "intermittent-reinforcement-relationships":
    "intermittent reinforcement and why it feels addictive",

  // Generational & childhood trauma
  "generational-trauma": "what generational trauma is and how to break the cycle",
  "signs-of-childhood-trauma-in-adults": "10 common signs of childhood trauma in adults",
  "childhood-emotional-neglect-signs-symptoms-healing": "childhood emotional neglect and its signs",
  "how-to-heal-from-childhood-trauma-as-an-adult": "recovering from childhood trauma as an adult",
  "walking-on-eggshells-growing-up": "growing up walking on eggshells",

  // Grounding & regulation
  "50-grounding-techniques-for-anxiety-ptsd-cptsd": "50 grounding techniques for anxiety, PTSD and CPTSD",
  "5-4-3-2-1-grounding-technique-guide": "the 5-4-3-2-1 grounding technique (the 54321 method)",
  "grounding-101-when-the-body-activates": "grounding basics for when the body activates",
  "how-to-regulate-your-nervous-system-after-trauma": "how to regulate your nervous system after trauma",
  "emotional-dysregulation-how-to-find-calm": "emotional dysregulation and how to find calm",
  "hypervigilance-why-you-always-feel-on-edge": "hypervigilance and always feeling on edge",
  "freeze-response-trauma-stuck": "the freeze response and feeling stuck",
  "trauma-and-sleep-why-its-hard-how-to-rest": "trauma and sleep, and how to rest again",
  "trauma-informed-self-care-daily-practice": "trauma-informed self-care as a daily practice",
  "where-is-trauma-stored-in-the-body": "where trauma is stored in the body",
  "depersonalisation-vs-derealisation": "the difference between depersonalisation and derealisation",
  "what-is-somatic-therapy": "what somatic therapy is and what a session involves",
  "what-is-emdr-therapy": "what EMDR therapy involves",
  "how-to-widen-your-window-of-tolerance": "how to widen your window of tolerance",
  "trauma-journal-how-to-start": "how to start a trauma recovery journal safely",
  "what-is-internal-family-systems-ifs": "Internal Family Systems (parts work) explained",
  "what-is-emotional-regulation": "what emotional regulation is and how to build it",

  // PTSD & CPTSD
  "what-is-ptsd": "what PTSD is, its symptoms and causes",
  "what-is-complex-ptsd": "what complex PTSD (CPTSD) is",
  "what-is-cptsd-understanding-complex-ptsd": "understanding complex PTSD",
  "cptsd-vs-ptsd": "the key differences between complex PTSD and PTSD",
  "ptsd-vs-cptsd-differences": "PTSD vs CPTSD",
  "emotional-flashbacks-cptsd": "emotional flashbacks and how to manage them",
  "ptsd-triggers": "how to identify and manage PTSD triggers",
  "living-with-ptsd-daily-coping": "daily coping strategies for living with PTSD",
  "can-you-recover-from-cptsd": "whether you can fully recover from CPTSD",
  "how-trauma-changes-the-brain": "how trauma changes the brain",
  "trauma-and-anxiety-connection": "the connection between trauma and anxiety",

  // Recovery basics
  // Overcoming trauma
  "how-to-overcome-trauma": "how to overcome trauma, step by step",
  "can-trauma-be-overcome": "whether trauma can really be overcome",
  "what-is-trauma": "what trauma actually is",
  "what-is-emotional-trauma": "what emotional trauma is",
  "signs-of-emotional-trauma-in-adults": "the signs of emotional trauma in adults",
  "how-to-overcome-betrayal-trauma": "how to overcome betrayal trauma",
  "why-is-childhood-trauma-so-hard-to-overcome":
    "why childhood trauma is so hard to overcome",
  "how-to-help-someone-overcome-trauma": "how to help someone overcome trauma",

  // Recovery basics
  "what-is-trauma-healing": "a complete guide to recovering after trauma",
  "beginners-guide-to-trauma-recovery": "a beginner's guide to trauma recovery",

  "phases-of-trauma-recovery": "the phases of trauma recovery",
  "how-long-does-trauma-recovery-take": "how long trauma recovery actually takes",
  "trauma-responses": "the four trauma responses",
  "hyper-independence-after-trauma": "hyper-independence and why asking for help feels unsafe",
  "how-to-stop-people-pleasing": "how to stop people-pleasing, step by step",
};

export const CLUSTERS: ClusterDef[] = [
  {
    id: "trauma-bonds",
    headTerm: "trauma bond",
    label: "Trauma bonds & abusive relationships",
    pillar: "trauma-bonding-explained",
    spokes: [
      "how-to-break-a-trauma-bond",
      "why-you-miss-them-withdrawal-after-trauma-bonding",
      "what-is-emotional-abuse-signs-examples",
      "the-cycle-of-abuse-explained",
      "trauma-bonding-with-a-parent",
      "love-bombing-the-first-red-flag",
      "narcissistic-abuse-recognising-the-patterns",
      "coercive-control-the-quiet-kind-of-abuse",
      "gaslighting-how-to-trust-yourself-again",
      "no-contact-vs-grey-rock",
      "why-its-so-hard-to-leave-an-abusive-relationship",
      "how-trauma-affects-relationships",
      "rebuilding-your-sense-of-self-after-abuse",
      "intermittent-reinforcement-relationships",
    ],

    bridges: [
      "hypervigilance-why-you-always-feel-on-edge",
      "how-to-regulate-your-nervous-system-after-trauma",
    ],
  },
  {
    id: "generational-childhood",
    headTerm: "generational trauma",
    label: "Generational & childhood trauma",
    pillar: "generational-trauma",
    spokes: [
      "signs-of-childhood-trauma-in-adults",
      "childhood-emotional-neglect-signs-symptoms-healing",
      "how-to-heal-from-childhood-trauma-as-an-adult",
      "walking-on-eggshells-growing-up",
      "trauma-bonding-with-a-parent",
    ],
    bridges: ["trauma-bonding-explained", "what-is-complex-ptsd"],
  },
  {
    id: "grounding-regulation",
    headTerm: "grounding techniques",
    label: "Grounding & nervous system regulation",
    pillar: "50-grounding-techniques-for-anxiety-ptsd-cptsd",
    spokes: [
      "5-4-3-2-1-grounding-technique-guide",
      "grounding-101-when-the-body-activates",
      "how-to-regulate-your-nervous-system-after-trauma",
      "emotional-dysregulation-how-to-find-calm",
      "hypervigilance-why-you-always-feel-on-edge",
      "freeze-response-trauma-stuck",
      "where-is-trauma-stored-in-the-body",
      "trauma-and-sleep-why-its-hard-how-to-rest",
      "trauma-informed-self-care-daily-practice",
      "depersonalisation-vs-derealisation",
      "what-is-somatic-therapy",
      "what-is-emdr-therapy",
      "how-to-widen-your-window-of-tolerance",
      "trauma-journal-how-to-start",
      "what-is-internal-family-systems-ifs",
      "what-is-emotional-regulation",
    ],
    bridges: ["hypervigilance-why-you-always-feel-on-edge", "emotional-flashbacks-cptsd"],
  },
  {
    id: "ptsd-cptsd",
    headTerm: "complex PTSD",
    label: "PTSD & complex PTSD",
    pillar: "what-is-ptsd",
    spokes: [
      "what-is-complex-ptsd",
      "cptsd-vs-ptsd",
      "ptsd-vs-cptsd-differences",
      "what-is-cptsd-understanding-complex-ptsd",
      "emotional-flashbacks-cptsd",
      "ptsd-triggers",
      "living-with-ptsd-daily-coping",
      "can-you-recover-from-cptsd",
      "how-trauma-changes-the-brain",
      "trauma-and-anxiety-connection",
    ],
    bridges: [
      "hypervigilance-why-you-always-feel-on-edge",
      "5-4-3-2-1-grounding-technique-guide",
    ],
  },
  {
    id: "overcoming-trauma",
    headTerm: "how to overcome trauma",
    label: "Overcoming trauma",
    pillar: "how-to-overcome-trauma",
    spokes: [
      "can-trauma-be-overcome",
      "what-is-trauma",
      "what-is-emotional-trauma",
      "signs-of-emotional-trauma-in-adults",
      "why-is-childhood-trauma-so-hard-to-overcome",
      "how-to-overcome-betrayal-trauma",
      "how-to-help-someone-overcome-trauma",
    ],
    bridges: [
      "what-is-trauma-healing",
      "how-to-regulate-your-nervous-system-after-trauma",
      "phases-of-trauma-recovery",
    ],
  },
  {

    id: "recovery-basics",
    headTerm: "trauma recovery",
    label: "Trauma recovery, step by step",
    pillar: "what-is-trauma-healing",
    spokes: [
      "beginners-guide-to-trauma-recovery",
      "phases-of-trauma-recovery",
      "how-long-does-trauma-recovery-take",
      "trauma-responses",
      "fawn-response-people-pleasing-trauma",
      "how-to-stop-people-pleasing",
      "hyper-independence-after-trauma",
    ],

    bridges: [
      "trauma-bonding-explained",
      "hypervigilance-why-you-always-feel-on-edge",
      "what-is-ptsd",
    ],
  },
];

export interface InternalLink {
  slug: string;
  anchor: string;
  /** True when the link points at the cluster pillar. */
  pillar?: boolean;
}

export interface ClusterPlacement {
  cluster: ClusterDef;
  isPillar: boolean;
  /** Link up to the pillar (null when this post is the pillar). */
  pillarLink: InternalLink | null;
  /** Sideways + bridge links, deduped and capped. */
  links: InternalLink[];
}

function anchorFor(slug: string): string {
  return ANCHORS[slug] ?? slug.replace(/-/g, " ");
}

/**
 * Resolve which cluster a post belongs to. A post can appear in several
 * clusters; the first match wins so every page has exactly one "home", which
 * keeps link equity flowing towards a single pillar rather than diluting it.
 */
export function clusterForSlug(slug: string): ClusterDef | null {
  return (
    CLUSTERS.find((c) => c.pillar === slug) ??
    CLUSTERS.find((c) => c.spokes.includes(slug)) ??
    null
  );
}

/**
 * Build the internal links to render on a given post.
 * - Spokes link up to the pillar first (strongest signal for the head term).
 * - Then the nearest siblings, rotated so different spokes surface different
 *   siblings and every post in the cluster receives inbound links.
 * - Then one or two bridges into an adjacent cluster.
 */
export function internalLinksFor(slug: string, max = 6): ClusterPlacement | null {
  const cluster = clusterForSlug(slug);
  if (!cluster) return null;

  const isPillar = cluster.pillar === slug;
  const pillarLink: InternalLink | null = isPillar
    ? null
    : { slug: cluster.pillar, anchor: anchorFor(cluster.pillar), pillar: true };

  const siblings = cluster.spokes.filter((s) => s !== slug);
  // Rotate the sibling list by this post's position so inbound links spread
  // evenly across the cluster instead of piling onto the first few spokes.
  const index = Math.max(0, cluster.spokes.indexOf(slug));
  const rotated = [...siblings.slice(index), ...siblings.slice(0, index)];

  const budget = max - (pillarLink ? 1 : 0);
  const bridgeCount = isPillar ? 2 : 1;
  const chosen = [
    ...rotated.slice(0, Math.max(0, budget - bridgeCount)),
    ...(cluster.bridges ?? []).filter((b) => b !== slug).slice(0, bridgeCount),
  ];

  const seen = new Set<string>([slug, cluster.pillar]);
  const links: InternalLink[] = [];
  for (const s of chosen) {
    if (seen.has(s)) continue;
    seen.add(s);
    links.push({ slug: s, anchor: anchorFor(s) });
  }

  return { cluster, isPillar, pillarLink, links: links.slice(0, budget) };
}
